import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/project-management';

const ProjectManagementLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Project Management Hub | Explanatum';
    const desc = 'Manage projects, tasks, and teams with AI-powered task generation, Gantt charts, and real-time reports—without Monday.com or Asana pricing.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Project management overview" className="relative overflow-hidden">
        <div className="absolute top-20 right-20 w-64 h-64 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40" aria-hidden="true" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-cyan-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Project Management Hub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Plan, track, and deliver projects with AI-powered intelligence.
                <span className="mt-2 block bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                  Enterprise PM features + 40+ business tools in one platform.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Build Kanban boards, Gantt timelines, custom reports, and dependency graphs while AI generates intelligent summaries and risk analysis. One subscription includes Project Management plus all Explanatum features—no hidden costs.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_pm_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start 7-day free trial
                </a>
                <a
                  href="/project-management"
                  className="inline-flex items-center justify-center rounded-full border-2 border-blue-600 px-6 py-3 text-base font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  View features
                </a>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                ✨ Includes all Explanatum features: PM Hub + AI Consultant + Cold Email + CRM + 40+ modules
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  AI summaries & risk detection
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Custom reports & PDF/Excel export
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Task dependencies & Gantt charts
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Project dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Project pulse</span>
                  <span>Live summary</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">🤖 AI generated 8 tasks from "Build mobile app" description • Estimated 120 hours • 3 sprints planned.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Team workload: Sarah 85% capacity • Mike 60% • Lisa 45% • 2 overdue tasks flagged with alerts.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Completion rate 78% • Gantt timeline shows launch date on track • Reports dashboard ready for stakeholders.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Enterprise features at startup pricing</h2>
            <p className="mt-4 text-base text-gray-500">135% feature parity with Monday.com Pro at 66% lower cost—plus AI capabilities they don't have.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: '🤖 AI Task Generation & Summaries',
              body: 'Generate tasks from descriptions, get AI-powered project summaries, risk detection with severity levels, and intelligent insights—all powered by Claude 3.5 Sonnet.'
            }, {
              title: '📊 Custom Reports & Analytics',
              body: 'Build unlimited custom reports with 7 metric types, multiple chart styles, advanced filtering, and schedule email delivery—no usage limits or extra fees.'
            }, {
              title: '📄 Professional Exports',
              body: 'Export to PDF with branded templates, Excel with auto-sized columns, CSV with proper encoding, and Gantt charts as high-quality images.'
            }, {
              title: '🔗 Task Dependencies & Critical Path',
              body: 'Visual dependency graph with drag-and-drop, cycle detection, 3 dependency types (blocks, blocked-by, related), and critical path highlighting.'
            }, {
              title: '🎨 Custom Fields & Templates',
              body: '6 field types (text, number, date, dropdown, checkbox, multiselect), save project templates with custom fields, and reuse across projects.'
            }, {
              title: '📧 Email Notifications & Digests',
              body: 'Professional HTML email templates for task assignments, comments, due date reminders, and daily digests—all included with no extra cost.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams switch from Monday.com and Asana</h2>
            <p className="mt-4 text-base text-gray-500">Get enterprise features without per-seat pricing, AI limitations, or complex integrations.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum (All Features Included)',
              points: [
                '🤖 AI summaries & risk detection (UNIQUE!)',
                'Custom reports builder with unlimited reports',
                'PDF/Excel/CSV export with branded templates',
                'Task dependencies with visual graph & cycle detection',
                'Custom fields (6 types) & project templates',
                'Email notifications & daily digests',
                'Fixed: £49/mo (3 users) includes PM + 40+ modules'
              ],
              accent: 'border-emerald-200 bg-emerald-50 text-gray-900'
            }, {
              title: 'Monday.com',
              points: [
                'No AI task generation capabilities',
                'Per-seat pricing starts at $8-12/user/month',
                'Automation limits on lower tiers',
                'Timeline view requires Business plan ($12+/user)',
                'Reporting limited without Enterprise tier',
                '3-user minimum = $24-36/month base cost',
                'Complex integrations for AI features'
              ],
              accent: 'border-gray-200 bg-white text-gray-900'
            }, {
              title: 'Asana',
              points: [
                'No AI task generation from descriptions',
                'Per-seat pricing: $10.99+/user/month',
                'Timeline view requires Premium plan',
                'Workload management only on Business tier ($24.99/user)',
                'Advanced reporting locked behind Business plan',
                '2-user minimum = $22+/month',
                'AI features still in beta with additional costs'
              ],
              accent: 'border-gray-200 bg-white text-gray-900'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-gray-900" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-blue-200 bg-blue-50 p-8 text-center">
            <h3 className="text-2xl font-semibold text-gray-900">"Ready to replace Monday.com and Asana?"</h3>
            <p className="mt-3 text-base text-gray-600">Get enterprise features + AI capabilities + 40+ business modules for one flat monthly fee. No per-seat charges, no surprise costs, no compromises.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_pm_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg hover:scale-[1.02]"
              >
                Start 7-day free trial
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              💳 No credit card required • 🎁 Full access to all features • ⚡ Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-gray-50 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where smart project management drives results</h2>
            <p className="mt-4 text-base text-gray-500">Give product, engineering, marketing, and operations teams one source of truth for every project.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Startups launching products',
              body: 'Plan sprints, track features, and coordinate across product, design, and engineering without Monday.com per-seat costs.'
            }, {
              title: 'Agencies managing clients',
              body: 'Organize client projects, assign deliverables, and track billable hours with workload management and time tracking.'
            }, {
              title: 'Marketing teams running campaigns',
              body: 'Plan content calendars, coordinate launches, and measure completion rates with real-time dashboards and automation.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white/60 p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AI Differentiation */}
      <section aria-label="AI features" className="relative overflow-hidden bg-white py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(139,92,246,0.18),transparent_55%)]" aria-hidden="true" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">AI-powered intelligence no competitor offers</h2>
            <p className="mt-4 text-base text-gray-500">5 unique AI features powered by Claude 3.5 Sonnet—included at no extra cost in your subscription.</p>
          </div>
          <div className="mt-12 space-y-8">
            <article className="rounded-3xl border border-purple-400/30 bg-purple-400/10 p-8 ">
              <div className="flex items-start gap-4">
                <span className="text-4xl">🤖</span>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">5 AI-Powered Capabilities (UNIQUE!)</h3>
                  <p className="mt-3 text-base text-gray-600">No competitor offers this comprehensive AI suite:</p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-500">
                    <li><strong className="text-gray-900">• Task Generation:</strong> Describe your project, get 5-10 intelligent tasks with priorities and time estimates</li>
                    <li><strong className="text-gray-900">• Project Summaries:</strong> AI generates executive summaries with progress, accomplishments, and recommendations</li>
                    <li><strong className="text-gray-900">• Risk Detection:</strong> Automatic identification of overdue tasks, team overload, and time variances with severity levels</li>
                    <li><strong className="text-gray-900">• Smart Insights:</strong> AI analyzes trends, identifies bottlenecks, and suggests improvements</li>
                    <li><strong className="text-gray-900">• Auto Descriptions:</strong> Generate detailed task descriptions from titles with acceptance criteria</li>
                  </ul>
                  <p className="mt-4 text-sm text-purple-200 font-medium">Saves 3-5 hours per project in planning and analysis. Monday.com charges $25+/user for basic AI features.</p>
                </div>
              </div>
            </article>

            <article className="rounded-3xl border border-blue-400/30 bg-blue-400/10 p-8 ">
              <div className="flex items-start gap-4">
                <span className="text-4xl">💎</span>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">One Subscription = Complete Business Platform</h3>
                  <p className="mt-3 text-base text-gray-600">Your monthly fee includes PM Hub PLUS all Explanatum features:</p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-500">
                    <li><strong className="text-gray-900">• AI Business Consultant:</strong> Strategic advice integrated with your project data</li>
                    <li><strong className="text-gray-900">• Cold Email Campaigns:</strong> Outreach automation with deliverability tools</li>
                    <li><strong className="text-gray-900">• CRM & Pipeline Management:</strong> Track deals and customer relationships</li>
                    <li><strong className="text-gray-900">• 40+ Business Modules:</strong> Marketing, sales, operations, and analytics tools</li>
                  </ul>
                  <p className="mt-4 text-sm text-blue-200 font-medium">Replace 5-10 separate subscriptions with one unified platform. Save $1,000+/month vs Monday.com + other tools.</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default ProjectManagementLanding;
